import { makeTypeGuardByProperty } from '../../../types/makeTypeGuardByProperty';
import type { vendorApiNames } from '../names';
import type {
  ApiKeyAuthentication,
  UsernamePasswordAuthentication,
} from './auth';
import type {
  BaseUpdateVendorApiSettings,
  BaseUrl,
  BaseVendorApi,
  RejectUnauthorized,
} from './base';
import type {
  VendorApiHttpAdvancedSettings,
  VendorApiHttpAgentSettings,
} from './http';
import type { VendorApiSessionSettings } from './session';

export interface SilverPeakBroadcastCliSettings {
  /**
   * Max request attempts count when a `broadcastCli` url task is not completed yet.
   */
  broadcastCliMaxRequestAttemptsCount?: number;
  /**
   * Retry time in milliseconds when a `broadcastCli` url task is not completed yet.
   */
  broadcastCliRetryAfterMs?: number;
}

export interface SilverPeakLoginType {
  loginType?: 'Local' | 'RADIUS' | 'TACACS+'; // Naming follows SilverPeak doc https://www.logicmonitor.com/support/silver-peak-orchestrator-monitoring
}

export type SilverPeakUsernamePasswordAuthentication =
  UsernamePasswordAuthentication & SilverPeakLoginType;

/**
 * SilverPeak API settings available from API
 */
export type PublicSilverPeakApiSettings = BaseVendorApi<
  (typeof vendorApiNames)['silverpeak']
> &
  RejectUnauthorized &
  BaseUrl &
  VendorApiHttpAgentSettings &
  VendorApiSessionSettings &
  VendorApiHttpAdvancedSettings &
  SilverPeakBroadcastCliSettings &
  SilverPeakLoginType;

/**
 * SilverPeak API settings available in backend
 */
export type SilverPeakApiSettings = PublicSilverPeakApiSettings &
  (SilverPeakUsernamePasswordAuthentication | ApiKeyAuthentication);

/**
 * SilverPeak API settings expected on API when updating
 */
export type UpdateSilverPeakApiSettings = BaseUpdateVendorApiSettings<
  PublicSilverPeakApiSettings,
  SilverPeakUsernamePasswordAuthentication | ApiKeyAuthentication
>;

export const isSilverPeakApiUsernameAuthentication = makeTypeGuardByProperty<
  Omit<SilverPeakApiSettings, 'isEnabled'>,
  SilverPeakApiSettings & SilverPeakUsernamePasswordAuthentication
>('username');

export const isSilverPeakApiKeyAuthentication = makeTypeGuardByProperty<
  Omit<SilverPeakApiSettings, 'isEnabled'>,
  SilverPeakApiSettings & ApiKeyAuthentication
>('apiKey');
